import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Eye, EyeOff, KeyRound, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { changeAdminPassword } from '../../lib/api';

/**
 * Auto-opens when admin/me returns must_change_password=true.
 * The user cannot dismiss until they have set a new strong password.
 */
export default function ForcePasswordChangeModal({ open, currentPassword, onSuccess }) {
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [cur, setCur] = useState(currentPassword || '');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (cur.length === 0) return toast.error('Enter your temporary password');
    if (pwd.length < 6) return toast.error('New password must be at least 6 characters');
    if (pwd !== pwd2) return toast.error('Passwords do not match');
    if (pwd === cur) return toast.error('Pick a password different from the temporary one');
    setBusy(true);
    try {
      await changeAdminPassword(cur, pwd);
      toast.success('Password updated. Welcome aboard!');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  // Important: no onOpenChange (cannot close until success)
  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-[460px] bg-[#1a1a1a] border border-[color:var(--gold)]/30 text-zinc-100 p-0 sm:rounded-xl [&>button]:hidden" data-testid="force-pwd-modal">
        <DialogTitle className="sr-only">Set Your Password</DialogTitle>
        <DialogDescription className="sr-only">You must set a new password before continuing.</DialogDescription>

        <div className="px-6 pt-6 pb-2 flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-400 shrink-0 mt-0.5">
            <ShieldAlert size={22} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Welcome — set your password</h2>
            <p className="text-zinc-400 text-xs mt-1">For security, you must replace the temporary password set by the Super Admin before you can use the panel.</p>
          </div>
        </div>

        <form onSubmit={submit} className="px-6 pb-6 pt-3 space-y-4">
          <div>
            <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">Temporary Password</label>
            <div className="relative">
              <Input type={showCur ? 'text' : 'password'} value={cur} onChange={(e) => setCur(e.target.value)} placeholder="The password your admin gave you" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 pr-10" data-testid="force-pwd-current-input" autoFocus />
              <button type="button" onClick={() => setShowCur((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                {showCur ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">New Password</label>
            <div className="relative">
              <Input type={showNew ? 'text' : 'password'} value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 6 characters" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100 pr-10" data-testid="force-pwd-new-input" />
              <button type="button" onClick={() => setShowNew((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200">
                {showNew ? <Eye size={18} /> : <EyeOff size={18} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[13px] font-semibold text-zinc-200 mb-1.5 block">Confirm New Password</label>
            <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} placeholder="Re-enter password" className="h-11 bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="force-pwd-confirm-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full h-11 bg-[color:var(--gold)] hover:brightness-110 text-black font-bold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="force-pwd-submit-btn">
            <KeyRound size={16} /> {busy ? 'Updating…' : 'Set Password'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
